/**
 * Upload Schema — V1 Suppliers (Bulk Upload)
 *
 * Creates suppliers via the V1 /entities endpoint.
 * Uses customSubmit for per-row POSTs since V1 has no bulk-create.
 */

import { UploadSchema } from '../types';

async function submitSupplier({
	row,
	projectId,
	apiBaseUrl,
	headers,
	signal,
}: {
	row: Record<string, unknown>;
	projectId: number;
	apiBaseUrl: string;
	headers: Record<string, string>;
	signal?: AbortSignal;
}): Promise<{ id: number; name?: string }> {
	// Build loans array for financing sources
	const loans: Array<{ id: number; parent: number | null }> = [];
	if (row.loanId) {
		loans.push({
			id: Number(row.loanId),
			parent: row.entityId ? Number(row.entityId) : null,
		});
	}

	const payload = {
		name: row.name,
		type: 'SUPPLIER',
		isKycVerified: true,
		project: projectId,
		loans,
		...(row.subtype ? { subtype: row.subtype } : {}),
		...(row.phone ? { phone: String(row.phone) } : {}),
		...(row.idExternal ? { idExternal: String(row.idExternal) } : {}),
		...(row.idWbg ? { idWbg: String(row.idWbg) } : {}),
	};

	const res = await fetch(`${apiBaseUrl}/v1/entities`, {
		method: 'POST',
		headers,
		body: JSON.stringify(payload),
		signal,
	});

	if (!res.ok) {
		const errText = await res.text().catch(() => `HTTP ${res.status}`);
		let msg: string;
		try {
			const errJson = JSON.parse(errText);
			msg = errJson.error?.message || errJson.message || `Supplier creation failed (${res.status})`;
		} catch {
			msg = errText || `Supplier creation failed (${res.status})`;
		}
		throw new Error(msg);
	}

	const data = await res.json();
	if (data.error) {
		throw new Error(data.error.message || 'Supplier creation failed');
	}

	const entity = data.data?.[0] ?? data.data ?? data;
	return { id: entity.id, name: entity.name ?? String(entity.id) };
}

export const v1SupplierSchema: UploadSchema = {
	entityType: 'v1Supplier',
	label: 'Supplier',
	apiEndpoint: '/v1/entities',
	apiMethod: 'POST',
	payloadTransform: (formValues, projectId) => {
		const loans: Array<{ id: number; parent: number | null }> = [];
		if (formValues.loanId) {
			loans.push({
				id: Number(formValues.loanId),
				parent: formValues.entity ? Number(formValues.entity) : null,
			});
		}

		return {
			name: formValues.name,
			type: 'SUPPLIER',
			isKycVerified: true,
			project: projectId,
			loans,
			...(formValues.subtype ? { subtype: formValues.subtype } : {}),
			...(formValues.phone ? { phone: String(formValues.phone) } : {}),
			...(formValues.idExternal ? { idExternal: String(formValues.idExternal) } : {}),
			...(formValues.idWbg ? { idWbg: String(formValues.idWbg) } : {}),
		};
	},
	customSubmit: submitSupplier,
	fields: [
		{
			name: 'name',
			label: 'Supplier Name',
			dataType: 'string',
			required: true,
			headerAliases: ['Entity Name', 'Vendor Name', 'Company Name', 'Supplier'],
		},
		{
			name: 'subtype',
			label: 'Subtype',
			dataType: 'enum',
			required: false,
			enumValues: ['INDIVIDUAL', 'ORGANIZATION', 'BANK', 'HOUSEHOLD', 'PRIVATE_COMPANY'] as const,
			headerAliases: ['Entity Subtype', 'Organization Type'],
		},
		{
			name: 'entityId',
			label: 'Entity',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'entity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'entities',
				alternateMatchFields: ['idExternal'],
			},
			headerAliases: ['Parent Entity', 'Borrower', 'PIU', 'Organization'],
		},
		{
			name: 'loanId',
			label: 'Financing Source',
			dataType: 'relation',
			required: false,
			relation: {
				entityType: 'loan',
				matchField: 'name',
				idField: 'id',
				contextKey: 'loans',
				alternateMatchFields: ['code'],
			},
			headerAliases: ['Loan', 'Loan Code', 'Financing', 'Fund Source'],
		},
		{
			name: 'idExternal',
			label: 'External ID',
			dataType: 'string',
			required: false,
			headerAliases: ['External Identifier', 'Ext ID', 'Reference'],
		},
		{
			name: 'phone',
			label: 'Phone',
			dataType: 'string',
			required: false,
			headerAliases: ['Phone Number', 'Telephone', 'Contact'],
		},
		{
			name: 'idWbg',
			label: 'WBG ID',
			dataType: 'string',
			required: false,
			headerAliases: ['World Bank ID', 'WB ID'],
		},
	],
};
